import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Sparkles, Share2, HelpCircle, ChevronRight, ChevronLeft,
  Images, NotebookPen, Search, Download, Smartphone, X,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * First-run onboarding tour — a comprehensive, chaptered walkthrough rendered
 * as SPOTLIGHT COACHMARKS: a dimmed full-screen backdrop with a highlighted
 * cut-out over the real on-screen control, plus a tooltip card pointing at it.
 *
 * Steps whose target control is actually on screen during an empty first-run
 * library (the + FAB, the search bar, the Share menu, Settings) are
 * spotlighted; steps that describe things living inside a not-yet-open flow
 * (AI auto-fill, the Icon Legend, the care log/growth timeline) gracefully
 * fall back to a centered card. Nine steps across three chapters:
 *
 *   CAPTURE          1. Add your first plant      → spotlight add-plant-fab
 *                    2. AI does the heavy lifting → centered
 *                    3. Add many at once          → spotlight add-plant-fab
 *   NURTURE          4. Decode the care icons     → centered
 *                    5. Log care & watch it grow  → centered
 *                    6. Find & organize anything  → spotlight search-input
 *   SHARE & ORGANIZE 7. Share your collection     → spotlight export-menu-button
 *                    8. Back up & export          → spotlight settings-button
 *                    9. Make it yours             → spotlight settings-button
 *
 * A couple of steps adapt wording to Local vs Google-Drive mode. The final
 * step offers three CTAs: add your first plant, read the full guide, dismiss.
 * Auto-triggered by LibraryPage on an empty library; replayable from Settings.
 * `localStorage.plant_onboarding_tour_seen='1'` prevents re-triggering.
 */

const SPOT_PAD = 10;
const CARD_W = 320;

export const OnboardingTour = ({ open, onClose, isLocalMode = false, onAddPlant }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [vp, setVp] = useState({ w: typeof window !== 'undefined' ? window.innerWidth : 1024, h: typeof window !== 'undefined' ? window.innerHeight : 768 });
  const cardRef = useRef(null);

  const chapters = [
    t('tourChapterCapture') || 'Capture',
    t('tourChapterNurture') || 'Nurture',
    t('tourChapterShare') || 'Share & Organize',
  ];

  const steps = [
    {
      chapter: 0, target: 'add-plant-fab',
      icon: Plus, iconColor: 'hsl(21, 90%, 52%)',
      title: t('tourStep1Title') || 'Add your first plant',
      body: t('tourStep1Body') || 'Tap the orange + button in the bottom-right corner to snap a photo or pick one from your gallery. The AI will identify the plant and fill in care details in seconds.',
    },
    {
      chapter: 0, target: null,
      icon: Sparkles, iconColor: 'hsl(143, 45%, 34%)',
      title: t('tourStep2Title') || 'AI does the heavy lifting',
      body: t('tourStep2Body') || 'The moment you add a plant, the AI identifies it and starts filling in watering, light, humidity, dormancy and more \u2014 automatically. You can keep browsing while it works in the background, even if your phone locks.',
    },
    {
      chapter: 0, target: 'add-plant-fab',
      icon: Images, iconColor: 'hsl(173, 45%, 34%)',
      title: t('tourCaptureBatchTitle') || 'Add many at once',
      body: t('tourCaptureBatchBody') || 'In a hurry? Pick several photos in one go \u2014 each becomes its own plant. You can also add multiple photos to a single plant, drag to reorder them and choose the cover shot. Re-adding a plant you already have? The app offers to merge the new photo in instead of making a duplicate.',
    },
    {
      chapter: 1, target: null,
      icon: HelpCircle, iconColor: 'hsl(40, 70%, 45%)',
      title: t('tourStep4Title') || 'Decode the care icons',
      body: t('tourStep4Body') || 'Every plant\u2019s detail view has a small ? Icon Legend button next to "Care Details" \u2014 tap it to see what every symbol means: watering, light, humidity, dormancy, and more. Handy when you\u2019re just getting started.',
    },
    {
      chapter: 1, target: null,
      icon: NotebookPen, iconColor: 'hsl(143, 45%, 34%)',
      title: t('tourNurtureLogTitle') || 'Log care & watch it grow',
      body: t('tourNurtureLogBody') || 'Open any plant to log waterings, feedings and notes on a running care timeline, and track progress with before/after growth photos. The app even follows each plant\u2019s dormancy so seasonal care stays on point.',
    },
    {
      chapter: 1, target: 'search-input',
      icon: Search, iconColor: 'hsl(210, 40%, 42%)',
      title: t('tourNurtureOrganizeTitle') || 'Find & organize anything',
      body: t('tourNurtureOrganizeBody') || 'Search by common or botanical name, then sort and group your library by family, type or your own order. Archive plants you no longer own without losing any of their history.',
    },
    {
      chapter: 2, target: 'export-menu-button',
      icon: Share2, iconColor: 'hsl(200, 60%, 42%)',
      title: t('tourStep3Title') || 'Share your collection',
      body: isLocalMode
        ? (t('tourShareBodyLocal') || 'Tap Share to create a read-only "Grow-along" link or QR code of your collection \u2014 no account needed for them to view. Add a warm welcome note to greet your visitors.')
        : (t('tourShareBodyDrive') || 'Tap Share to send a friend a read-only "Grow-along" link \u2014 no account needed to view. Add a warm welcome note, or show a QR code so anyone nearby can scan and browse your greens.'),
    },
    {
      chapter: 2, target: 'settings-button',
      icon: Download, iconColor: 'hsl(173, 45%, 34%)',
      title: t('tourExportTitle') || 'Back up & export',
      body: isLocalMode
        ? (t('tourExportBodyLocal') || 'Everything lives privately on this device. Export a polished PDF care sheet or a CSV backup whenever you like \u2014 perfect for safekeeping or moving your collection to another device.')
        : (t('tourExportBodyDrive') || 'Your library and photos sync safely to your own Google Drive automatically, so nothing is ever lost. Need a copy? Export a polished PDF care sheet or a CSV of your whole collection anytime.'),
    },
    {
      chapter: 2, target: 'settings-button',
      icon: Smartphone, iconColor: 'hsl(265, 40%, 48%)',
      title: t('tourPersonalizeTitle') || 'Make it yours',
      body: isLocalMode
        ? (t('tourPersonalizeBodyLocal') || 'Switch to dark mode, choose your language, and install the app to your home screen like a native app. It works fully offline \u2014 ideal for a greenhouse or balcony with no signal.')
        : (t('tourPersonalizeBodyDrive') || 'Switch to dark mode, choose your language, and install the app to your home screen like a native app. It works offline too \u2014 any changes you make sync automatically the moment you\u2019re back online.'),
    },
  ];

  const isLast = step === steps.length - 1;
  const current = steps[step];

  // Measure the target control for the current step (null => centered card).
  const measure = useCallback(() => {
    setVp({ w: window.innerWidth, h: window.innerHeight });
    const targetId = steps[step]?.target;
    if (!targetId) { setRect(null); return; }
    const el = document.querySelector(`[data-testid="${targetId}"]`);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) { setRect(null); return; }
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (!open) return undefined;
    measure();
    const t1 = setTimeout(measure, 140);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      clearTimeout(t1);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, step, measure]);

  // Lock body scroll while the tour is open so spotlight targets stay put.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const finish = useCallback(() => {
    try { localStorage.setItem('plant_onboarding_tour_seen', '1'); } catch (_e) { /* ignore */ }
    setStep(0);
    setRect(null);
    onClose();
  }, [onClose]);

  const next = () => { if (isLast) finish(); else setStep((s) => s + 1); };
  const back = () => setStep((s) => Math.max(0, s - 1));
  const handleAddFirst = () => { finish(); if (onAddPlant) onAddPlant(); };
  const handleReadGuide = () => { finish(); navigate('/about'); };

  // Move focus into the card on each step and trap Tab inside it.
  useEffect(() => {
    if (!open) return undefined;
    const id = setTimeout(() => {
      const el = cardRef.current?.querySelector('[data-tour-autofocus]') || cardRef.current?.querySelector('button');
      el?.focus();
    }, 70);
    return () => clearTimeout(id);
  }, [open, step]);

  const onKeyDown = (e) => {
    if (e.key !== 'Tab') return;
    const nodes = cardRef.current?.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])');
    if (!nodes || nodes.length === 0) return;
    const list = Array.from(nodes);
    const first = list[0];
    const last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  // Close on Escape from anywhere — a document-level listener so it works even
  // when focus is still on the trigger (e.g. replaying from the Settings modal).
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (e.key === 'Escape') { e.preventDefault(); finish(); } };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, finish]);

  if (!open) return null;

  const spotlight = !!rect;

  // Card position. For spotlight steps, place near the target (below if the
  // target sits in the top half, otherwise above) and clamp into the viewport.
  let cardStyle;
  let placeAbove = false;
  if (spotlight) {
    placeAbove = rect.top + rect.height / 2 > vp.h / 2;
    const centerX = rect.left + rect.width / 2;
    const left = Math.min(Math.max(centerX - CARD_W / 2, 12), Math.max(12, vp.w - CARD_W - 12));
    cardStyle = placeAbove
      ? { left, bottom: Math.max(12, vp.h - (rect.top - SPOT_PAD) + 14), width: CARD_W }
      : { left, top: rect.top + rect.height + SPOT_PAD + 14, width: CARD_W };
  } else {
    cardStyle = { left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: Math.min(CARD_W + 16, vp.w - 24) };
  }

  const Icon = current.icon;

  const overlay = (
    <div
      className="fixed inset-0"
      style={{ zIndex: 1000 }}
      data-testid="onboarding-tour"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-title"
      aria-describedby="tour-body"
      onKeyDown={onKeyDown}
    >
      {/* Click-blocker + dim (used when there is no spotlight hole). */}
      <button
        type="button"
        aria-label={t('skip') || 'Skip'}
        onClick={finish}
        className="absolute inset-0 w-full h-full cursor-default"
        style={{ backgroundColor: spotlight ? 'transparent' : 'rgba(15, 30, 20, 0.66)', border: 'none' }}
        tabIndex={-1}
        data-testid="tour-backdrop"
      />

      {/* Spotlight hole: a transparent rect with a huge spread shadow that
          dims everything around the highlighted control. */}
      {spotlight && (
        <div
          className="absolute"
          style={{
            top: rect.top - SPOT_PAD,
            left: rect.left - SPOT_PAD,
            width: rect.width + SPOT_PAD * 2,
            height: rect.height + SPOT_PAD * 2,
            borderRadius: 16,
            boxShadow: '0 0 0 9999px rgba(15, 30, 20, 0.66)',
            outline: '3px solid rgba(255,255,255,0.95)',
            outlineOffset: '0px',
            pointerEvents: 'none',
            transition: 'top 0.25s ease, left 0.25s ease, width 0.25s ease, height 0.25s ease',
            zIndex: 1001,
          }}
          data-testid="tour-spotlight"
        />
      )}

      {/* Click shield over the highlighted control: swallows taps so the
          spotlighted element is NOT a live link during the tour — tapping it
          neither dismisses the tour nor triggers the underlying button. */}
      {spotlight && (
        <div
          className="absolute"
          style={{
            top: rect.top - SPOT_PAD,
            left: rect.left - SPOT_PAD,
            width: rect.width + SPOT_PAD * 2,
            height: rect.height + SPOT_PAD * 2,
            borderRadius: 16,
            cursor: 'default',
            zIndex: 1001,
          }}
          onClick={(e) => { e.stopPropagation(); }}
          aria-hidden="true"
          data-testid="tour-spotlight-shield"
        />
      )}

      {/* Tooltip / coachmark card */}
      <div
        ref={cardRef}
        className="absolute rounded-2xl shadow-2xl overflow-hidden"
        style={{ ...cardStyle, zIndex: 1002, backgroundColor: 'white' }}
      >
        {/* Little arrow pointing at the target (spotlight steps only) */}
        {spotlight && (
          <div
            className="absolute w-3 h-3 rotate-45"
            style={{
              backgroundColor: 'white',
              left: Math.min(Math.max((rect.left + rect.width / 2) - (cardStyle.left) - 6, 16), CARD_W - 28),
              ...(placeAbove ? { bottom: -6 } : { top: -6 }),
            }}
            aria-hidden="true"
          />
        )}

        <div className="p-5">
          {/* Chapter label + close */}
          <div className="flex items-center justify-between mb-2">
            <span
              className="text-[11px] font-semibold tracking-widest uppercase"
              style={{ color: 'hsl(143, 35%, 42%)' }}
              data-testid="tour-chapter-label"
            >
              {chapters[current.chapter]}
            </span>
            <button
              type="button"
              onClick={finish}
              aria-label={t('skip') || 'Skip'}
              className="p-1 rounded-full transition-colors hover:bg-gray-100"
              style={{ color: 'hsl(125, 12%, 45%)' }}
              data-testid="tour-skip-btn"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>

          {/* Title with inline icon */}
          <div className="flex items-start gap-2.5 mb-2">
            <span
              className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: 'hsl(143, 35%, 95%)' }}
            >
              <Icon className="w-4.5 h-4.5" style={{ color: current.iconColor, width: 18, height: 18 }} aria-hidden="true" />
            </span>
            <h2
              id="tour-title"
              className="text-base font-semibold leading-snug"
              style={{ color: 'hsl(143, 28%, 22%)' }}
              data-testid={`tour-title-${step}`}
            >
              {current.title}
            </h2>
          </div>

          <p
            id="tour-body"
            className="text-sm leading-relaxed"
            style={{ color: 'hsl(125, 12%, 32%)' }}
            data-testid={`tour-body-${step}`}
          >
            {current.body}
          </p>

          {/* Progress dots */}
          <div className="flex items-center gap-1.5 mt-4" data-testid="tour-progress-dots">
            {steps.map((s, i) => (
              <span
                key={i}
                className="rounded-full transition-all"
                style={{
                  width: i === step ? '18px' : '6px',
                  height: '6px',
                  marginLeft: i > 0 && s.chapter !== steps[i - 1].chapter ? '8px' : '0',
                  backgroundColor: i === step ? 'hsl(143, 45%, 34%)' : 'hsl(143, 20%, 88%)',
                }}
                aria-hidden="true"
              />
            ))}
            <span className="ml-auto text-xs" style={{ color: 'hsl(125, 12%, 50%)' }}>
              {step + 1} / {steps.length}
            </span>
          </div>

          {/* Final-step CTAs */}
          {isLast && (
            <div className="mt-4 space-y-2">
              {onAddPlant && (
                <button
                  type="button"
                  onClick={handleAddFirst}
                  className="w-full flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-full shadow-sm transition-all hover:opacity-95"
                  style={{ backgroundColor: 'hsl(21, 90%, 55%)', color: 'white' }}
                  data-testid="tour-add-plant-btn"
                >
                  <Plus className="w-4 h-4" strokeWidth={3} aria-hidden="true" />
                  {t('tourAddFirstPlant') || 'Add your first plant'}
                </button>
              )}
              <button
                type="button"
                onClick={handleReadGuide}
                className="w-full text-xs underline transition-opacity hover:opacity-70"
                style={{ color: 'hsl(143, 28%, 38%)' }}
                data-testid="tour-about-link"
              >
                {t('tourReadFullGuide') || 'Read the full guide'}
              </button>
            </div>
          )}

          {/* Nav row */}
          <div className="flex items-center justify-between gap-2 mt-4">
            <button
              type="button"
              onClick={finish}
              className="text-xs px-1 py-1.5 rounded transition-colors hover:opacity-70"
              style={{ color: 'hsl(125, 12%, 45%)' }}
              data-testid="tour-skip-text-btn"
            >
              {t('skip') || 'Skip'}
            </button>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <button
                  type="button"
                  onClick={back}
                  className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border transition-colors hover:bg-gray-50"
                  style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}
                  data-testid="tour-back-btn"
                >
                  <ChevronLeft className="w-3.5 h-3.5" aria-hidden="true" />
                  {t('back') || 'Back'}
                </button>
              )}
              <button
                type="button"
                onClick={next}
                data-tour-autofocus
                className="flex items-center gap-1 text-sm px-4 py-1.5 rounded-full font-medium transition-all"
                style={{ backgroundColor: 'hsl(143, 45%, 34%)', color: 'white' }}
                data-testid={isLast ? 'tour-done-btn' : 'tour-next-btn'}
              >
                {isLast ? (t('done') || 'Got it') : (t('next') || 'Next')}
                {!isLast && <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

export default OnboardingTour;
